import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { PrimeNG } from 'primeng/config';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import { CatEditService } from 'src/app/services/cat-edit.service';
import { CatUploadService } from 'src/app/services/cat-upload.service';
import { StorageService } from 'src/app/services/storage.service';
import { LoaderService } from 'src/app/services/loader.service';
import { DateFormetService } from 'src/app/services/dateFormet.service';
import { OrderConfirmationDetailsComponent } from 'src/app/shared-components/order-confirmation-details/order-confirmation-details.component';
import { TemplateEditComponent } from '../../cat-edit/template-edit/template-edit.component';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { buildOrderFileName } from 'src/app/shared-components/order-file-name.util';
import { OrderService } from 'src/app/services/order.service';
export interface DocumentItem {
  id: string;
  type: 'pdf' | 'image' | 'template';
  name: string;
  file?: File;
  templateId?: number;
  base64?: string;
  pageCount?: number;
  order: number;
}

@Component({
  selector: 'app-cat-compose-form',
  templateUrl: './cat-compose-form.component.html',
  styleUrls: ['./cat-compose-form.component.scss'],
  standalone: false,
})
export class CatComposeFormComponent implements OnInit, OnDestroy {
  // ── Modal refs ────────────────────────────────────────────────────────────────
  @ViewChild('reviewOrder') reviewOrder: TemplateRef<any>;
  @ViewChild('paymentScreen') paymentScreen: TemplateRef<any>;
  @ViewChild('orderDetails') orderDetails!: OrderConfirmationDetailsComponent;
  @ViewChild('pdfInput') pdfInput!: ElementRef<HTMLInputElement>;
  @ViewChild('imageInput') imageInput!: ElementRef<HTMLInputElement>;

  // ── Step visibility ───────────────────────────────────────────────────────────
  showCompose = true;
  showEditTemplate = false;
  showEstampDetails = false;
  showEsignDetails = false;
  showConfirmation = false;

  // ── Step active (stepper colour) ─────────────────────────────────────────────
  active_compose = false;
  active_template = false;
  active_eStamp = false;
  active_eSign = false;
  active_confirm = false;

  // ── Step data (same pattern as cat-edit) ─────────────────────────────────────
  selectedBranch: any;
  productDetailsData: any;
  sendEditTemplateData: any;
  editTemplateData: any;
  estampData: any;
  esignData: any = { toBeEsigned: true };
  revertedStampData: any;
  orderConfirmationData: any = { service: 'CAT-Compose' };
  reviewOrderData: any;
  paymentOrderData: any;
  paymentDetails: {
    payeeName: string;
    payeeEmail: string;
    payeeMobileNumber: string;
    payNow: string;
    paymentMethod: string;
  };

  // ── Flags copied from cat-edit ────────────────────────────────────────────────
  aadhaar_validation: any;
  order_reviewer: any;
  otp_validation: boolean;
  userRole: string;
  order_payment = true;
  stateCode = '';
  isSignerEnable: any;
  account_id: any;

  // ── Step-1 form ───────────────────────────────────────────────────────────────
  composeForm: FormGroup;
  enableRefIdField = false;
  showHdfcFields = false;
  noBranch = false;
  submitted = false;

  // ── Document compose state ────────────────────────────────────────────────────
  selectedDocuments: DocumentItem[] = [];
  availableTemplates: any[] = [];
  filteredTemplates: any[] = [];
  templateSearchText = '';
  showDropdown = false;
  isDragging = false;
  isLoadingTemplates = false;

  pdfUploading = false;
  imageUploading = false;

  previewModalVisible: boolean = false;
  previewModalMode: 'upload' | 'preview' = 'preview';
  previewFile: File | null = null;
  previewModalFiles: File[] = [];
  previewPdfSrc: any = null;
  previewFileName: string = '';
  previewFileType: string = 'pdf';
  previewImageUrl: string | null = null;
  previewHtmlContent: string | null = null;
  previewIsTemplate: boolean = false;
  activePreviewDoc: DocumentItem | null = null;
  stagedComposeFiles: File[] = [];
  stagedComposeType: 'pdf' | 'image' = 'pdf';

  onPreviewVisibilityChange(visible: boolean) {
    this.previewModalVisible = visible;
    if (!visible) {
      this.previewIsTemplate = false;
      this.activePreviewDoc = null;
      this.previewModalMode = 'preview';
      this.cdr.detectChanges();
    }
  }

  async openDocPreview(doc: DocumentItem) {
    this.previewModalMode = 'preview';
    this.activePreviewDoc = doc;
    this.previewModalFiles = doc.file ? [doc.file] : [];
    this.previewFileName = doc.name;
    this.previewFileType = doc.type;
    this.previewIsTemplate = doc.type === 'template' || !!doc.templateId;
    this.previewImageUrl = null;
    this.previewHtmlContent = null;
    this.previewFile = null;
    this.previewPdfSrc = null;

    if (doc.type === 'pdf') {
      this.previewIsTemplate = false;
      if (doc.file) {
        this.previewFile = doc.file;
      } else if (doc.base64) {
        this.previewPdfSrc = `data:application/pdf;base64,${doc.base64}`;
      }
      this.previewModalVisible = true;
      this.cdr.detectChanges();
    } else if (doc.type === 'image') {
      this.previewIsTemplate = false;
      if (doc.file) {
        this.previewImageUrl = URL.createObjectURL(doc.file);
      } else if (doc.base64) {
        this.previewImageUrl = `data:image/png;base64,${doc.base64}`;
      }
      this.previewModalVisible = true;
      this.cdr.detectChanges();
    } else if (doc.type === 'template') {
      this.previewIsTemplate = true;
      if (doc.templateId) {
        if (doc.base64) {
          this.previewPdfSrc = `data:application/pdf;base64,${doc.base64}`;
          this.previewFileType = 'pdf';
          this.previewIsTemplate = true;
          this.previewModalVisible = true;
          this.cdr.detectChanges();
          return;
        }

        this.loader.show();
        this.orderService
          .getEditTemplatePDFDocument({ template_id: doc.templateId, fields: [] })
          .subscribe({
            next: (resp: any) => {
              this.loader.hide();
              const pdfBase64 = resp?.content?.document_url;
              if (pdfBase64) {
                doc.base64 = pdfBase64;
                this.previewPdfSrc = `data:application/pdf;base64,${pdfBase64}`;
                this.previewFileType = 'pdf';
                this.previewIsTemplate = true;
                this.previewModalVisible = true;
              } else {
                this.previewFallbackTemplateHtml(doc);
              }
              this.cdr.detectChanges();
            },
            error: () => {
              this.loader.hide();
              this.previewFallbackTemplateHtml(doc);
            },
          });
      } else {
        this.previewFallbackTemplateHtml(doc);
      }
    }
  }

  private previewFallbackTemplateHtml(doc: DocumentItem): void {
    this.previewIsTemplate = true;
    if (doc.templateId) {
      this.catEditService
        .getTemplate(`?account_id=${this.account_id}&template_id=${doc.templateId}`)
        .subscribe({
          next: (res: any) => {
            const td = res?.content?.[0];
            this.previewHtmlContent = td?.template_html || `<p>Template: ${doc.name}</p>`;
            this.previewFileType = 'template';
            this.previewIsTemplate = true;
            this.previewModalVisible = true;
            this.cdr.detectChanges();
          },
          error: () => {
            this.previewHtmlContent = `<p>Template: ${doc.name}</p>`;
            this.previewFileType = 'template';
            this.previewIsTemplate = true;
            this.previewModalVisible = true;
            this.cdr.detectChanges();
          },
        });
    } else {
      this.previewHtmlContent = `<p>Template: ${doc.name}</p>`;
      this.previewFileType = 'template';
      this.previewIsTemplate = true;
      this.previewModalVisible = true;
      this.cdr.detectChanges();
    }
  }

  onComposeFileSaved(event: { file: File; base64: string }) {
    let doc = this.activePreviewDoc;
    if (!doc || doc.name !== event.file.name) {
      doc = this.selectedDocuments.find((d) => d.name === event.file.name) || doc;
    }
    if (doc) {
      doc.file = event.file;
      doc.name = event.file.name;
      doc.base64 = event.base64;
      this.activePreviewDoc = doc;
      this.previewFileName = doc.name;
      this.catEditService
        .uploadComposeFile(event.file, this.sessionId, this.account_id)
        .subscribe({
          next: () => {},
          error: () => {},
        });
      this.cdr.detectChanges();
    }
  }

  readonly sessionId: string = `session_${Date.now()}_${Math.random().toString(36).slice(2)}`;

  private branchSub: Subscription;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private catEditService: CatEditService,
    private catUploadService: CatUploadService,
    private storageService: StorageService,
    private loader: LoaderService,
    private dateFormetService: DateFormetService,
    private messageService: MessageService,
    private primeng: PrimeNG,
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef,
    private orderService: OrderService,
  ) {
    this.storageService.setContractType(false);
    this.order_reviewer = this.storageService.getOrderReviewConsent() || null;
    this.otp_validation = !!this.storageService.getOtpValidation();
    this.userRole = this.storageService.getRole() || null;
    this.aadhaar_validation = this.storageService.getAdharEdit() || null;
    this.showHdfcFields =
      (this.storageService.getOrganization() || '') === 'HDHO';

    this.storageService.getSignerValue.subscribe((res) => {
      this.isSignerEnable = res;
    });

    this.composeForm = this.fb.group({
      branchName: ['Selected Branch', Validators.required],
      referenceNumber: [
        '',
        [
          Validators.required,
          Validators.maxLength(80),
          this.referenceIdValidator(),
        ],
      ],
    });
  }

  ngOnInit(): void {
    this.account_id = this.storageService.getAccountId() || null;
    this.primeng.ripple.set(true);
    this.otp_validation = !!this.storageService.getOtpValidation();
    this.setBranch();
    this.getRefIdConsent();
    this.loadTemplates();
  }

  ngOnDestroy(): void {
    this.branchSub?.unsubscribe();
  }

  private _isDraggingCard = false;

  @HostListener('dragstart')
  onCardDragStart() {
    this._isDraggingCard = true;
  }

  @HostListener('dragend')
  onCardDragEnd() {
    this._isDraggingCard = false;
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent) {
    if (this._isDraggingCard) {
      return;
    }
    event.preventDefault();
    event.returnValue = '';
  }

  // ── Validators ────────────────────────────────────────────────────────────────
  referenceIdValidator(): ValidatorFn {
    return (c: AbstractControl): ValidationErrors | null =>
      !c.value || /^[a-zA-Z0-9_-]*$/.test(c.value)
        ? null
        : { invalidChars: true };
  }

  // ── Branch ────────────────────────────────────────────────────────────────────
  setBranch() {
    const b = this.storageService.getBranch();
    if (b?.name) this.composeForm.controls['branchName'].setValue(b.name);
    this.branchSub = this.storageService.branchChange.subscribe(
      (branch: any) => {
        this.selectedBranch = branch;
        this.composeForm.controls['branchName'].setValue(
          branch?.name || 'Selected Branch',
        );
      },
    );
  }

  changeState(data: any) {
    this.selectedBranch = this.storageService.getBranch() || null;
    this.productDetailsData = data;
  }

  // ── Reference ID ─────────────────────────────────────────────────────────────
  getRefIdConsent() {
    this.enableRefIdField = this.storageService.getRefIdConsent() === true;
    if (!this.enableRefIdField) {
      this.catEditService.getOrValidateReferenceId().subscribe(
        (res: any) => {
          if (res.status === 200)
            this.composeForm.controls['referenceNumber'].setValue(
              res.content.reference_id,
            );
        },
        () => {},
      );
    }
  }

  validateAndProceed() {
    const refId = this.composeForm.value.referenceNumber;
    this.catEditService.getOrValidateReferenceId(refId).subscribe(
      (res: any) => {
        if (res.status === 200) this.proceedToEditTemplate();
      },
      (err: any) => {
        this.messageService.add({
          severity: 'error',
          detail: err?.error?.message || 'Reference ID validation failed',
        });
      },
    );
  }

  onReferencePaste(event: ClipboardEvent): void {
    if (!this.enableRefIdField) {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    const text = event.clipboardData?.getData('text') || '';
    const sanitized = text.replace(/[^a-zA-Z0-9_-]/g, '');
    const input = event.target as HTMLInputElement;
    const s = input.selectionStart ?? 0,
      e = input.selectionEnd ?? 0;
    this.composeForm
      .get('referenceNumber')
      ?.setValue(input.value.slice(0, s) + sanitized + input.value.slice(e));
  }

  // ── Templates ─────────────────────────────────────────────────────────────────
  loadTemplates() {
    this.isLoadingTemplates = true;
    this.catEditService.getTemplate(`?account_id=${this.account_id}`).subscribe(
      (res: any) => {
        this.availableTemplates = res['content'] || [];
        this.filteredTemplates = [...this.availableTemplates];
        this.isLoadingTemplates = false;
      },
      () => {
        this.isLoadingTemplates = false;
        this.messageService.add({
          severity: 'warn',
          detail: 'Templates unavailable. PDF/image upload still works.',
        });
      },
    );
  }

  // ── Dropdown ──────────────────────────────────────────────────────────────────
  toggleDropdown() {
    this.showDropdown = !this.showDropdown;
  }
  closeDropdown() {
    this.showDropdown = false;
  }

  // ── PDF upload ────────────────────────────────────────────────────────────────

  /** Opens the hidden <input type="file" multiple accept=".pdf"> dialog */
  triggerPdfInput() {
    this.pdfInput.nativeElement.click();
  }

  onPdfSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) this.processPdfFiles(Array.from(input.files));
    input.value = '';
  }

  processPdfFiles(files: File[]) {
    const MAX_BYTES = 10 * 1024 * 1024;

    // ── Step 1: local validation — fast, no network ──────────────────────────
    const valid: File[] = [];
    const invalid: string[] = [];

    for (const file of files) {
      const isPdf =
        file.type === 'application/pdf' ||
        file.name.toLowerCase().endsWith('.pdf');

      if (!isPdf) {
        invalid.push(`"${file.name}" is not a PDF`);
        continue;
      }
      if (file.size > MAX_BYTES) {
        invalid.push(
          `"${file.name}" exceeds 10 MB (${(file.size / 1024 / 1024).toFixed(1)} MB)`,
        );
        continue;
      }
      if (
        this.selectedDocuments.some(
          (d) => d.name === file.name && d.type === 'pdf',
        )
      ) {
        invalid.push(`"${file.name}" is already added`);
        continue;
      }
      valid.push(file);
    }

    for (const msg of invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'File skipped',
        detail: msg,
        life: 4500,
      });
    }

    if (!valid.length) return;

    // ── Step 2: Stage for preview in upload mode (same as e-sign flow) ───
    this.stagedComposeFiles = [...valid];
    this.stagedComposeType = 'pdf';
    this.previewModalMode = 'upload';
    this.previewFileType = 'pdf';
    this.previewIsTemplate = false;
    this.previewImageUrl = null;
    this.previewHtmlContent = null;
    this.previewPdfSrc = null;

    if (valid.length > 1) {
      this.previewFile = null;
      this.previewModalFiles = [...valid];
      this.previewFileName = valid.map(f => f.name).join(', ');
    } else {
      this.previewFile = valid[0];
      this.previewModalFiles = [valid[0]];
      this.previewFileName = valid[0].name;
    }

    this.previewModalVisible = true;
    this.cdr.detectChanges();
  }

  // ── Image upload ──────────────────────────────────────────────────────────────

  triggerImageInput() {
    this.imageInput.nativeElement.click();
  }

  onImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) this.processImageFiles(Array.from(input.files));
    input.value = '';
  }

  processImageFiles(files: File[]) {
    const MAX_BYTES = 10 * 1024 * 1024;
    const VALID_MIME = new Set(['image/jpeg', 'image/jpg', 'image/png']);
    const VALID_EXT = new Set(['.jpg', '.jpeg', '.png']);

    // ── Step 1: local validation ─────────────────────────────────────────────
    const valid: File[] = [];
    const invalid: string[] = [];

    for (const file of files) {
      const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
      const isImage = VALID_MIME.has(file.type) || VALID_EXT.has(ext);

      if (!isImage) {
        invalid.push(`"${file.name}" must be JPG or PNG`);
        continue;
      }
      if (file.size > MAX_BYTES) {
        invalid.push(
          `"${file.name}" exceeds 10 MB (${(file.size / 1024 / 1024).toFixed(1)} MB)`,
        );
        continue;
      }
      if (
        this.selectedDocuments.some(
          (d) => d.name === file.name && d.type === 'image',
        )
      ) {
        invalid.push(`"${file.name}" is already added`);
        continue;
      }
      valid.push(file);
    }

    for (const msg of invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'File skipped',
        detail: msg,
        life: 4500,
      });
    }

    if (!valid.length) return;

    // ── Step 2: Stage for preview in upload mode (same as e-sign flow) ───
    this.stagedComposeFiles = [...valid];
    this.stagedComposeType = 'image';
    this.previewModalMode = 'upload';
    this.previewFileType = 'image';
    this.previewIsTemplate = false;
    this.previewHtmlContent = null;
    this.previewPdfSrc = null;
    this.previewFile = valid[0];
    this.previewModalFiles = [valid[0]];
    this.previewFileName = valid[0].name;
    this.previewImageUrl = URL.createObjectURL(valid[0]);

    this.previewModalVisible = true;
    this.cdr.detectChanges();
  }

  readAsBase64(file: File, type: 'pdf' | 'image') {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const doc: DocumentItem = {
        id: `${type}_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        type,
        name: file.name,
        file,
        base64: btoa(e.target.result),
        order: this.selectedDocuments.length,
      };
      this.selectedDocuments = [...this.selectedDocuments, doc];
      this.cdr.detectChanges();
    };
    reader.readAsBinaryString(file);
  }

  onComposeUploadCancelled() {
    this.stagedComposeFiles = [];
    this.previewModalVisible = false;
    this.previewModalMode = 'preview';
    this.previewIsTemplate = false;
    this.cdr.detectChanges();
  }

  onComposeUploadConfirmed(event: {
    file: File;
    files?: File[];
    base64?: string;
    arrayBuffer?: ArrayBuffer;
  }) {
    const filesToUpload: File[] =
      event.files && event.files.length > 0
        ? event.files
        : this.stagedComposeFiles.length > 0
          ? this.stagedComposeFiles
          : event.file
            ? [event.file]
            : [];

    if (!filesToUpload.length) {
      this.previewModalMode = 'preview';
      this.stagedComposeFiles = [];
      return;
    }

    const type = this.stagedComposeType;
    if (type === 'pdf') {
      this.pdfUploading = true;
    } else {
      this.imageUploading = true;
    }
    this.cdr.detectChanges();

    const uploads$ = filesToUpload.map((file) =>
      this.catEditService
        .uploadComposeFile(file, this.sessionId, this.account_id)
        .pipe(
          catchError((err) =>
            of({
              __error: true,
              __file: file,
              __msg: err?.error?.message || err?.message || 'Upload failed',
            }),
          ),
        ),
    );

    forkJoin(uploads$)
      .pipe(
        finalize(() => {
          this.pdfUploading = false;
          this.imageUploading = false;
          this.previewModalMode = 'preview';
          this.stagedComposeFiles = [];
          this.cdr.detectChanges();
        }),
      )
      .subscribe((results: any[]) => {
        const succeeded: File[] = [];
        const failedMsgs: string[] = [];

        results.forEach((res, i) => {
          if (res?.__error) {
            failedMsgs.push(`"${(res.__file as File).name}": ${res.__msg}`);
          } else {
            succeeded.push(filesToUpload[i]);
          }
        });

        for (const file of succeeded) {
          this.readAsBase64(file, type);
        }

        if (succeeded.length > 0 && failedMsgs.length === 0) {
          this.messageService.add({
            severity: 'success',
            summary: type === 'pdf' ? 'PDFs uploaded' : 'Images uploaded',
            detail: `${succeeded.length} ${type === 'pdf' ? 'PDF' : 'image'}${succeeded.length > 1 ? 's' : ''} uploaded successfully`,
            life: 3500,
          });
        } else if (succeeded.length > 0 && failedMsgs.length > 0) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Partial upload',
            detail: `${succeeded.length} uploaded, ${failedMsgs.length} failed`,
            life: 5000,
          });
          for (const msg of failedMsgs) {
            this.messageService.add({
              severity: 'error',
              summary: 'Upload failed',
              detail: msg,
              life: 6000,
            });
          }
        } else {
          for (const msg of failedMsgs) {
            this.messageService.add({
              severity: 'error',
              summary: 'Upload failed',
              detail: msg,
              life: 6000,
            });
          }
        }
        this.cdr.detectChanges();
      });
  }

  selectTemplate(template: any) {
    const templateAlreadySelected = this.selectedDocuments.some(
      (d) => d.type === 'template',
    );

    if (templateAlreadySelected) {
      this.messageService.add({
        severity: 'info',
        detail: 'Only one template can be selected.',
      });
      return;
    }

    const templateDoc: DocumentItem = {
      id: `template_${template.id}_${Date.now()}`,
      type: 'template',
      name: template.name,
      templateId: template.id,
      order: 0,
    };

    this.selectedDocuments = [
      templateDoc,
      ...this.selectedDocuments.map((doc, index) => ({
        ...doc,
        order: index + 1,
      })),
    ];
    this.showDropdown = false;
    this.openDocPreview(templateDoc);
  }

  isTemplateSelected(id: number): boolean {
    return this.selectedDocuments.some(
      (d) => d.templateId === id && d.type === 'template',
    );
  }

  onTemplateSearch(event: Event) {
    const q = (event.target as HTMLInputElement).value.toLowerCase();
    this.templateSearchText = q;
    this.filteredTemplates = q
      ? this.availableTemplates.filter((t) => t.name.toLowerCase().includes(q))
      : [...this.availableTemplates];
  }

  // ── Drag & Drop ───────────────────────────────────────────────────────────────
  onDragOver(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    this.isDragging = true;
  }
  onDragLeave(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    this.isDragging = false;
  }
  onDrop(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    this.isDragging = false;
    const files = Array.from(e.dataTransfer?.files || []);
    // Route through the same validation + BE-1 upload pipeline
    const pdfs = files.filter(
      (f) =>
        f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'),
    );
    const images = files.filter((f) => {
      const ext = f.name.toLowerCase().slice(f.name.lastIndexOf('.'));
      return (
        ['image/jpeg', 'image/jpg', 'image/png'].includes(f.type) ||
        ['.jpg', '.jpeg', '.png'].includes(ext)
      );
    });
    if (pdfs.length) this.processPdfFiles(pdfs);
    if (images.length) this.processImageFiles(images);
  }
  drop(event: CdkDragDrop<DocumentItem[]>) {
    // Template should always remain at first position
    if (event.previousIndex === 0 || event.currentIndex === 0) {
      return;
    }

    moveItemInArray(
      this.selectedDocuments,
      event.previousIndex,
      event.currentIndex,
    );

    this.selectedDocuments = this.selectedDocuments.map((item, index) => ({
      ...item,
      order: index,
    }));
  }
  removeDoc(id: string) {
    this.selectedDocuments = this.selectedDocuments
      .filter((d) => d.id !== id)
      .map((d, idx) => ({ ...d, order: idx }));
  }

  // ── Step 1 → Next ─────────────────────────────────────────────────────────────
  onNextClick() {
    this.submitted = true;
    if (this.composeForm.value.branchName === 'Selected Branch') {
      this.noBranch = true;
      this.messageService.add({
        severity: 'warn',
        detail: 'Please select a branch',
      });
      return;
    }
    if (this.composeForm.invalid) {
      this.messageService.add({
        severity: 'warn',
        detail: 'Please fill all required fields',
      });
      return;
    }
    if (this.selectedDocuments.length === 0) {
      this.messageService.add({
        severity: 'warn',
        detail: 'Add at least one document or template',
      });
      return;
    }
    this.enableRefIdField
      ? this.validateAndProceed()
      : this.proceedToEditTemplate();
  }

proceedToEditTemplate() {
  const referenceNumber = this.composeForm.value.referenceNumber;
  const template = this.selectedDocuments.find(
    (d) => d.type === 'template'
  );
  if (!template) {
    this.messageService.add({
      severity:'warn',
      detail:'Please select a template'
    });
    return;
  }
  const params =
  `?account_id=${this.account_id}&template_id=${template.templateId}`;
  this.catEditService
  .getTemplate(params)
  .subscribe((res:any)=>{
    const td = res?.content?.[0];
    if(td){
    if(td.template_html){
        td.template_html =
        td.template_html.replace(
          /<body([^>]*)>/,
          '<body$1 style="zoom:0.70;">'
        );
      }
  this.productDetailsData = {
        ...td,
        referenceNumber,
        composedDocuments: this.selectedDocuments,
        name:`Composed_${referenceNumber}`,
        isComposed:true
      };
      this.sendEditTemplateData = {
        ...td,
        composedDocuments:this.selectedDocuments,
        isComposed:true
      };
     this.gotoScreen('editTemplate');
     this.cdr.detectChanges();
    }
  });
}

  // ── Step 2 Edit Template ──────────────────────────────────────────────────────
  oneditTemplateChange(data: any): void {
    this.editTemplateData = data;
    this.esignData.pdfData = this.editTemplateData.pdfData;
    this.esignData.composedDocuments = this.selectedDocuments;
    
      // add this
  if (this.sendEditTemplateData) {
    this.sendEditTemplateData.fileds = data.fileds;
    this.sendEditTemplateData.form = data.form;
    this.sendEditTemplateData.html = data.html;
    this.sendEditTemplateData.pdfData = data.pdfData;
  }

    let page = this.editTemplateData.page;
    if (page === 'product') {
      page = 'compose';
    }
    this.gotoScreen(page);
  }

  // ── Step 3 E-stamp ────────────────────────────────────────────────────────────
  onestampDataChange(data: any): void {
    this.estampData = data;
    this.revertedStampData = data;
    if (data.isEstamped) {
      this.stateCode = data.state_code || data.selectedState?.state_code || '';
    }
    if (this.estampData.page === 'editTemplate') {
      this.sendEditTemplateData.form = this.editTemplateData.form;
      this.sendEditTemplateData.html = this.editTemplateData.html;
    }
    let page = this.estampData.page || 'editTemplate';
    if (!page) {
      page = 'compose';
    }
    this.gotoScreen(page);
  }

  // ── Step 4 E-sign ─────────────────────────────────────────────────────────────
  onesignDataChange(data: any): void {
    this.order_payment = data.paymentbySigner;
    this.paymentDetails = {
      payeeName: data.payeeName,
      payeeEmail: data.payeeEmail,
      payeeMobileNumber: data.payeeMobileNumber,
      payNow: data.payNowOrLater,
      paymentMethod: data.paymentMethod,
    };
    this.esignData = data;
    const page = data.page;
    if (page === 'estamp') {
      this.esignData.toBeEsigned = true;
      this.estampData = this.revertedStampData;
      this.gotoScreen(page);
    } else {
      this.setAllOrderDetails();
    }
  }

  // ── Assemble & Place Order (identical to cat-edit) ────────────────────────────
  setAllOrderDetails() {
    this.selectedBranch =
      this.estampData?.branch || this.storageService.getBranch() || null;
    this.estampData = this.estampData.map((e: any) => {
      const { page, ...rest } = e;
      return rest;
    });
    const esignsDetals = {
      party_users: this.esignData.party_users
        ? [...this.esignData.party_users]
        : [],
      witness_users: this.esignData.witness_users
        ? [...this.esignData.witness_users]
        : [],
    };

    if (esignsDetals.party_users.length) {
      esignsDetals.party_users = esignsDetals.party_users.map((el: any) => ({
        name: el.name,
        email: el.email,
        aadhaar_digits:
          el.method === 'AADHAAR' && this.aadhaar_validation
            ? el.aadhaar_digits
            : '',
        contact_number: el.contact_number,
        sign_position: el.sign_position,
        method: el.method,
        position_details: el.position_details,
        is_authorised: !!el.is_authorised,
        authorised_for: el.is_authorised ? (el.authorised_for || '') : '',
        pages: el.pages,
        signatory_sequence: el.signatory_sequence,
        send_document: el.send_document,
        send_notification: el.send_notification,
        remark: el.remark,
        reminder: el.reminder,
        schedule_timestamp: this.dateFormetService.formatDateTime(
          el.schedule_timestamp,
        ),
        server_side_siganture: el.server_side_siganture,
        estamp_sign: (this.storageService.getStampSignatory() && this.storageService.getDocumentToBeStamped() !== false) ? (el.estamp_sign || false) : false,
        esign_otp: this.otp_validation,
        cc_emails: [
          this.esignData.carbon_copy_0,
          this.esignData.carbon_copy_1,
        ].filter((e: any) => !!e),
        sign_mode:
          el.method === 'DSC'
            ? 'USB-Token'
            : el.method === 'ELECTRONIC'
              ? ''
              : el.sign_mode,
      }));
    }

    const orderData = {
      referance_id: this.productDetailsData.referenceNumber,
      account_id: this.account_id,
      branch_id: this.selectedBranch.id,

      template_value:
        this.editTemplateData?.form?.fieldDataForms?.map((item: any) => ({
          key: item.id_attribute,
          value: item.value_attribute,
        })) || [],

      uploaded_documents: this.selectedDocuments
        .filter((doc: any) => doc.type !== 'template')
        .map((doc: any) => ({
          type: doc.type,
          base64: doc.base64,
        })),

      estamps: this.estampData,
      esigns: esignsDetals,
    };

    if (
      orderData.estamps.length === 0 &&
      orderData.esigns.party_users.length === 0 &&
      orderData.esigns.witness_users.length === 0
    ) {
      this.messageService.add({
        severity: 'error',
        detail: 'Estamp and E-sign details cannot be empty',
      });
      return;
    }

    const data = {
      template_id: this.productDetailsData.id,
      is_bulk: false,
      file_name: buildOrderFileName(
        this.productDetailsData.referenceNumber,
        this.productDetailsData.name ?? null,
      ),
      order_details: [orderData],
    };

    const reviewData = { serviceType: 'CAT Compose', details: data };
    this.reviewOrderData = reviewData;

    if (this.isSignerEnable && this.order_payment) {
      this.getPayment(data);
    } else if (this.order_reviewer) {
      this.onApproveMethod();
    } else {
      this.placeOrderData(data);
    }
  }

  // ── Place Order ───────────────────────────────────────────────────────────────
  placeOrderData(data: any) {
    if (this.order_payment && this.isSignerEnable) {
      this.getPayment(data);
    } else {
      this.catEditService.postOrder(data).subscribe(
        (res) => {
          if (res) {
            this.getOrderConfirmation(res.content.order_id);
            this.messageService.add({
              severity: 'success',
              detail: 'Order Placed Successfully!',
            });
            this.gotoScreen('confirmation');
          }
        },
        (err) => {
          const errors = err?.error?.content?.errors || [];
          errors.forEach((e: string) =>
            this.messageService.add({ severity: 'error', detail: e }),
          );
        },
      );
    }
  }

  getOrderConfirmation(orderId: any) {
    this.loader.show();
    this.catEditService
      .getOrderConfirmation({ order_ids: orderId.toString(), detail: 1 })
      .subscribe((res: any) => {
        if (res.status === 200) {
          this.catUploadService.orderDetails = res.content[0].order;
          this.orderDetails.getPlacedOrderDetails();
        }
      });
  }

  closeModel() {
    this.modalService.dismissAll();
  }

  // ── Review Order ──────────────────────────────────────────────────────────────
  reviewOrderDataChange(data: any) {
    this.closeModel();
    if (data.path === 'order') this.placeOrderData(data.details);
  }

  onApproveMethod() {
    this.modalService.open(this.reviewOrder, {
      size: 'xl',
      ariaLabelledBy: 'modal-basic-title',
      backdrop: 'static',
      keyboard: false,
    });
  }
  // ── Payment ───────────────────────────────────────────────────────────────────
  getPayment(obj: any) {
    this.paymentOrderData = {
      orderDetails: obj,
      payment: this.paymentDetails,
      stateCode: this.stateCode,
      service_type: 'Cat-Compose',
    };
    this.modalService.open(this.paymentScreen, {
      size: 'xl',
      ariaLabelledBy: 'modal-basic-title',
      backdrop: 'static',
      keyboard: false,
    });
  }

  paymentOrderDataChange(event: any) {
    if (event.status === 200) {
      this.messageService.add({
        severity: 'success',
        summary: 'Order Placed successfully!',
        detail: event.message,
      });
      this.closeModel();
    }
  }

  // ── Order Confirmation ────────────────────────────────────────────────────────
  onOrderConfirmationChange(data: any): void {
    this.orderConfirmationData = data;
  }

  // ── Screen / Stepper nav ──────────────────────────────────────────────────────
  gotoScreen(value: string): void {
    this.showCompose = value === 'compose';
    this.showEditTemplate = value === 'editTemplate';
    this.showEstampDetails = value === 'estamp';
    this.showEsignDetails = value === 'esign';
    this.showConfirmation = value === 'confirmation';
    this.setActiveTab(value);
  }

  setActiveTab(value: string) {
    if (value === 'compose') {
      this.active_compose = false;
      this.active_template = false;
      this.active_eStamp = false;
      this.active_eSign = false;
      this.active_confirm = false;
    } else {
      this.active_compose = true;
      this.active_template = value !== 'editTemplate';
      this.active_eStamp = value !== 'editTemplate' && value !== 'estamp';
      this.active_eSign =
        value !== 'editTemplate' && value !== 'estamp' && value !== 'esign';
      this.active_confirm = value === 'confirmation';
    }
  }

  // ── Utilities ─────────────────────────────────────────────────────────────────
  back() {
    this.userRole === 'CLIENT'
      ? this.router.navigate(['doqfy/dashboard'])
      : this.router.navigate(['doqfy/admin-dashboard']);
  }

  iconFor(type: string): string {
    return type === 'pdf'
      ? 'bxs-file-pdf'
      : type === 'image'
        ? 'bxs-image'
        : 'bxs-file-doc';
  }
}
